import { Link } from "@tanstack/react-router";

import { articleBySlug } from "../lib/content";
import {
  type Passage,
  passageBySlug,
  passageMinutes,
  passageOutline,
} from "../lib/passage";
import { verseTopicBySlug } from "../lib/verses";
import { formatDate } from "./ArticleBody";
import { Contents, FaqList, renderBlock, ShortAnswer } from "./ProseBlocks";
import { StoreCta } from "./StoreCta";

/**
 * One famous passage, explained.
 *
 * ── The order of the page is the argument ────────────────────────────────────
 *
 * The passage itself is first, before a single word of ours. Somebody who
 * searched for this reference wants the words, and a page that makes them read
 * three paragraphs of introduction to reach a sentence they already half know
 * has failed the visit even if it ranks.
 *
 * The context verses come next, QUOTED rather than summarised, because
 * everything the page then argues rests on them — and a page that asserts what
 * the surrounding verses say is asking to be trusted where a page that prints
 * them is not.
 *
 * The correction (`misreading`) is a required field on the type and gets its
 * own visually distinct block. It is the reason this page exists next to fifty
 * that reprint the greeting card, and burying it in the prose would be hiding
 * the only part that is ours.
 */

function ContextVerses({ verses }: { verses: Passage["context"] }) {
  return (
    <section className="mt-12">
      <h2
        id="in-context"
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        In context
      </h2>
      <p className="mt-4 leading-[1.75] text-body">
        The verses around it, printed rather than described — everything below
        rests on them.
      </p>
      {verses.map((verse, i) => renderBlock({ type: "verse", verse }, i))}
    </section>
  );
}

function Misreading({ misreading }: { misreading: Passage["misreading"] }) {
  return (
    <section className="mt-14">
      <h2
        id="what-it-does-not-say"
        className="scroll-mt-24 font-display text-[clamp(1.4rem,3.5vw,1.9rem)] leading-tight font-bold text-ink"
      >
        What it is usually taken to mean
      </h2>
      {/* The claim is stated fairly and set apart, so nobody has to guess which
          reading is being answered — and so the answer cannot quietly move the
          goalposts halfway through. */}
      <blockquote className="brutal mt-5 bg-inset p-5 font-display text-lg leading-snug font-bold text-ink sm:p-6">
        {misreading.claim}
      </blockquote>
      {misreading.correction.map((paragraph) => (
        <p
          key={paragraph.slice(0, 40)}
          className="mt-5 leading-[1.75] text-body"
        >
          {paragraph}
        </p>
      ))}
    </section>
  );
}

function RelatedPassages({ slugs }: { slugs: string[] }) {
  const passages = slugs.map(passageBySlug).filter((p) => p !== undefined);
  if (passages.length === 0) return null;
  return (
    <section className="mt-14 border-t-[2.5px] border-ink pt-10">
      <h2 className="font-display text-2xl font-bold text-ink">
        More passages
      </h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {passages.map((item) => (
          <li key={item.slug}>
            <Link
              to="/verse/$slug"
              params={{ slug: item.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
                {item.reference}
              </span>
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {item.h1}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RelatedElsewhere({
  topics,
  articles,
}: {
  topics: string[];
  articles: string[];
}) {
  const collections = topics
    .map(verseTopicBySlug)
    .filter((t) => t !== undefined);
  const pieces = articles.map(articleBySlug).filter((a) => a !== undefined);
  if (collections.length === 0 && pieces.length === 0) return null;
  return (
    <section className="mt-12">
      <h2 className="font-display text-2xl font-bold text-ink">Read next</h2>
      <ul className="mt-5 grid gap-4 sm:grid-cols-2">
        {collections.map((topic) => (
          <li key={topic.slug}>
            <Link
              to="/verses/$slug"
              params={{ slug: topic.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {topic.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {topic.description}
              </span>
            </Link>
          </li>
        ))}
        {pieces.map((piece) => (
          <li key={piece.slug}>
            <Link
              to="/blog/$slug"
              params={{ slug: piece.slug }}
              className="brutal flex h-full flex-col gap-2 p-5 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg leading-snug font-bold text-ink">
                {piece.h1}
              </span>
              <span className="text-sm leading-relaxed text-ink2">
                {piece.description}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function PassagePage({ passage }: { passage: Passage }) {
  return (
    <article className="mx-auto w-full max-w-[68ch]">
      <p className="font-display text-xs font-bold tracking-[0.16em] text-scripture uppercase">
        {formatDate(passage.published)}
        {passage.updated ? ` · updated ${formatDate(passage.updated)}` : ""} ·{" "}
        {passageMinutes(passage)} min read
      </p>
      <h1 className="mt-3 font-display text-[clamp(1.9rem,6vw,2.9rem)] leading-[1.15] font-bold tracking-tight text-ink text-balance">
        {passage.h1}
      </h1>

      {/* The passage before anything of ours. */}
      {renderBlock({ type: "verse", verse: passage.verse }, 0)}

      <p className="mt-6 text-[clamp(1.05rem,2.4vw,1.2rem)] leading-relaxed text-ink2">
        {passage.standfirst}
      </p>

      <ShortAnswer takeaways={passage.takeaways} />
      <Contents headings={passageOutline(passage)} />

      <ContextVerses verses={passage.context} />

      {passage.body.map(renderBlock)}

      <Misreading misreading={passage.misreading} />

      {passage.use.map(renderBlock)}

      {passage.faq ? <FaqList items={passage.faq} /> : null}

      <StoreCta from={`verse:${passage.slug}`} />

      <RelatedPassages slugs={passage.relatedPassages ?? []} />
      <RelatedElsewhere
        topics={passage.relatedTopics ?? []}
        articles={passage.related}
      />
    </article>
  );
}
